import { useTranslate, HttpError } from '@refinedev/core';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Tabs, Divider } from 'antd';
import type { SeriesDetailOut, SeriesIn } from '@/api/generated/schemas';
import {
  ModStatus,
  CollectionSortRuleInDirection,
} from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';
import { CollectionSortingShow } from '@/components/query-builder';
import { OrderedPublicationCardsField } from '@/components/user_catalog/OrderedPublicationCardsField';
import { normalizeOrderedRows } from '@/components/user_catalog/orderedRows';

// The publication order is built into this kind and cannot be configured;
// the API reports it back on every read. Kept here so the create form can
// show it before the first save.
const BUILTIN_SORTING = [
  { field: 'series_position', direction: CollectionSortRuleInDirection.asc },
];

export const SeriesCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps } = useForm<
    SeriesDetailOut,
    HttpError,
    SeriesIn
  >({
    resource: 'user_catalog.series',
  });

  const handleFinish = (values: SeriesIn) =>
    formProps.onFinish?.({
      ...values,
      series_publications: normalizeOrderedRows(
        values.series_publications,
        'publication_id'
      ),
    });

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.series.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Divider>
                    {t('user_catalog.series.fields.series_publications')}
                  </Divider>

                  <OrderedPublicationCardsField name="series_publications" />

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                    initialValue={ModStatus.enabled}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.series.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.series.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields />

                  <Divider>{t('base.collection.sorting')}</Divider>
                  <Form.Item extra={t('base.collection.sorting_builtin_hint')}>
                    <CollectionSortingShow sorting={BUILTIN_SORTING} />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              forceRender: true,
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Create>
  );
};
